import React, { useEffect, useRef, useState } from 'react';
import * as PropTypes from 'prop-types';
import { displayToast } from '../../Util/Toast';

const ASPECT_RATIO = 1.6;
const OUTPUT_MIME_TYPE = 'image/webp';
const OUTPUT_QUALITY = 0.8;
const OUTPUT_FILE_NAME = 'blog-article-header.webp';
// twice the widest header box, so retina screens stay sharp
const MAX_OUTPUT_WIDTH = 1792;

const HeaderImageInput = ({
  initialImageUrl = null,
  altText = '',
  onImageChange = () => {},
  onAltTextChange = () => {}
}) => {
  const [previewUrl, setPreviewUrl] = useState(initialImageUrl);
  const [cropSourceUrl, setCropSourceUrl] = useState(null);
  const fileInput = useRef();
  const cropImage = useRef();
  const cropper = useRef(null);
  const previewObjectUrl = useRef(null);

  useEffect(() => {
    if (!cropSourceUrl) {
      return undefined;
    }

    let cancelled = false;
    // loaded on demand to keep cropperjs out of the main bundle
    import('cropperjs').then(({ default: Cropper }) => {
      if (cancelled) {
        return;
      }
      cropper.current = new Cropper(cropImage.current, {
        aspectRatio: ASPECT_RATIO,
        viewMode: 1,
        autoCropArea: 1,
        zoomable: false
      });
    });

    return () => {
      cancelled = true;
      if (cropper.current) {
        cropper.current.destroy();
        cropper.current = null;
      }
    };
  }, [cropSourceUrl]);

  useEffect(() => {
    return () => {
      if (previewObjectUrl.current) {
        URL.revokeObjectURL(previewObjectUrl.current);
      }
    };
  }, []);

  const showPreview = (blob) => {
    if (previewObjectUrl.current) {
      URL.revokeObjectURL(previewObjectUrl.current);
    }
    previewObjectUrl.current = blob ? URL.createObjectURL(blob) : null;
    setPreviewUrl(previewObjectUrl.current);
  };

  const openFilePicker = () => {
    return fileInput.current.click();
  };

  const closeCropper = () => {
    URL.revokeObjectURL(cropSourceUrl);
    setCropSourceUrl(null);
  };

  const handleFileInput = (event) => {
    const [file] = event.target.files;
    if (!file) {
      return;
    }
    setCropSourceUrl(URL.createObjectURL(file));
    // reset so picking the same file again still fires onChange
    fileInput.current.value = '';
  };

  const applyCrop = () => {
    if (!cropper.current) {
      return;
    }

    cropper.current
      .getCroppedCanvas({
        maxWidth: MAX_OUTPUT_WIDTH,
        maxHeight: MAX_OUTPUT_WIDTH / ASPECT_RATIO
      })
      .toBlob(
        (blob) => {
          if (!blob) {
            displayToast('Could not process the image', 'error');
            return;
          }
          showPreview(blob);
          onImageChange(
            new File([blob], OUTPUT_FILE_NAME, { type: OUTPUT_MIME_TYPE })
          );
          closeCropper();
        },
        OUTPUT_MIME_TYPE,
        OUTPUT_QUALITY
      );
  };

  const deleteImage = () => {
    showPreview(null);
    onImageChange(null);
    onAltTextChange('');
  };

  return (
    <div className="flex flex-col gap-3">
      <span className="font-semibold text-gray-800 text-lg">Header image</span>

      <div className="relative w-full aspect-[1.6/1] bg-gray-200 rounded-md overflow-hidden flex items-center justify-center">
        {previewUrl ? (
          <>
            <img
              src={previewUrl}
              alt={altText || 'Header image preview'}
              className="w-full h-full object-cover"
            />
            <button
              type="button"
              className="absolute top-4 right-4 bg-gray-200 shadow-md text-gray-800 rounded-md p-2 inline-flex items-center justify-center"
              onClick={deleteImage}
            >
              <span className="material-icons">delete</span>
            </button>
          </>
        ) : (
          <button
            type="button"
            className="bg-gray-200 text-gray-800 rounded-md py-2 px-4 text-base font-semibold"
            onClick={openFilePicker}
          >
            Upload an image
          </button>
        )}
      </div>

      <input
        ref={fileInput}
        onChange={handleFileInput}
        type="file"
        accept="image/png, image/jpg, image/jpeg, image/webp"
        className="hidden"
      />

      {previewUrl && (
        <>
          <button
            type="button"
            className="self-start text-blue-600 font-semibold text-base underline"
            onClick={openFilePicker}
          >
            Replace image
          </button>

          <label
            htmlFor="blog-image-alt-text"
            className="font-semibold text-gray-800 text-lg"
          >
            Image alt text
            <input
              id="blog-image-alt-text"
              type="text"
              maxLength={255}
              className="text-left p-3 border w-full border-gray-300 placeholder-gray-200 mt-2 rounded-sm focus:outline-hidden focus:border-blue-500 font-normal"
              onChange={(e) => {
                return onAltTextChange(e.target.value);
              }}
              value={altText}
            />
          </label>
        </>
      )}

      {cropSourceUrl && (
        <div
          className="fixed inset-0 z-[60] bg-white bg-opacity-95 flex items-center justify-center p-8"
          role="dialog"
        >
          <div className="bg-white w-full max-w-3xl flex flex-col gap-4">
            <h2 className="text-lg font-bold text-gray-800">
              Crop header image
            </h2>
            <div>
              <img
                ref={cropImage}
                src={cropSourceUrl}
                alt="Crop source"
                className="max-w-full"
                style={{ maxHeight: '60vh' }}
              />
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                className="rounded-md bg-gray-200 text-gray-800 font-semibold text-base px-4 py-2"
                onClick={closeCropper}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-md bg-blue-600 text-white font-semibold text-base px-4 py-2"
                onClick={applyCrop}
              >
                Apply crop
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeaderImageInput;

HeaderImageInput.propTypes = {
  initialImageUrl: PropTypes.string,
  altText: PropTypes.string,
  onImageChange: PropTypes.func,
  onAltTextChange: PropTypes.func
};
